<template>
  <a-form layout="vertical" :model="formState" ref="formRef">
    <DisabledContainer :isDisabled="disabled">
      <a-form-item
        label="Archivo de audio"
        name="file_audio"
        class="audio-form-item-mb0"
        :rules="[{ required: true, message: 'Por favor ingresar archivo de audio' }]"
        :validateStatus="validateAudioPickerStatus"
      >
        <ArchivePicker
          v-model:value="formState.file_audio"
          :supportedTypes="['.mp3']"
          :maxMBSize="3"
        />
      </a-form-item>
    </DisabledContainer>
    <AudioPreview
      v-if="formState.file_audio"
      :node-src="node?.file_audio_url"
      :file="formState.file_audio"
    />

    <DisabledContainer :isDisabled="disabled">
      <a-divider class="divider" />
      <p>
        Las variables capturadas se guardarán en la información del ticket. También, podrás
        visualizarlas en el reporte de este IVR.
      </p>

      <a-form-item
        name="variable_name"
        required
        :rules="[{ required: true, message: 'Por favor ingresar nombre de la variable' }]"
      >
        <template #label>
          Nombre de la variable
          <a-tooltip
            title="Este nombre es únicamente informativo. Los usuarios podrán visualizarlo en la información del ticket del contacto."
          >
            <InfoCircleOutlined class="info-icon" />
          </a-tooltip>
        </template>

        <a-input v-model:value="formState.variable_name" placeholder="Escribir nombre" />
      </a-form-item>

      <ActionPicker v-if="node" :node="node" :parentNode="parentNode" :disabled="disabled" />
    </DisabledContainer>

    <div v-if="formState.action == ActionType.ConnectorApi">
      <a-divider class="divider" />

      <h3>
        Configuración del conector de API
      </h3>

      <DisabledContainer :isDisabled="disabled">
        <a-form-item
          label="Selecciona tu conector de API"
          name="api_connector"
          :rules="[{ required: true, message: 'Por favor selecciona un conector de API' }]"
        >
          <a-select
            v-model:value="formState.api_connector"
            placeholder="Seleccione la acción"
            :options="optionsConnectorApi"
          />
        </a-form-item>
      </DisabledContainer>

      <a-form-item
        v-if="formState.api_connector"
        label="Valores del tipo numérico"
        class="form-item"
        name="connector_value"
      >
        <a-input
          v-if="connectorSelected?.data_type == ResponseType.Number"
          :value="connectorSelected?.data_value"
          disabled
        />
        <a-input v-else placeholder="Verdadero/falso" disabled />
      </a-form-item>
    </div>

    <a-divider class="divider" />

    <h3>
      Restricciones para validación de respuesta
      <a-switch v-model:checked="formState.has_validation" size="small" @change="key++" />
    </h3>

    <div :key="key">
      <a-form-item
        label="Número de dígitos que debe tener la respuesta"
        name="max_digit_answer"
        :rules="[
          { required: formState.has_validation, message: 'Por favor ingresar número dígitos' },
        ]"
      >
        <a-input-number
          v-model:value="formState.max_digit_answer"
          :disabled="!formState.has_validation"
          class="input"
          :placeholder="!formState.has_validation ? '0' : '1'"
          min="0"
          :precision="0"
        />
      </a-form-item>

      <DisabledContainer :isDisabled="disabled">
        <a-form-item
          name="wrong_answer_file_audio"
          class="audio-form-item-mb0"
          :rules="[
            { required: hasWrongAnswerAudio, message: 'Por favor ingresar archivo de audio' },
          ]"
          :validateStatus="validateAudioPickerStatusWrongAnswer"
        >
          <template #label>
            Archivo de audio por respuesta incorrecta

            <a-tooltip title="Este audio se reproducirá tras los primeros tres intentos equívocos.">
              <InfoCircleOutlined class="info-icon" />
            </a-tooltip>
          </template>

          <ArchivePicker
            v-model:value="formState.wrong_answer_file_audio"
            :supportedTypes="['.mp3']"
            :disabled="!formState.has_validation"
            :maxMBSize="3"
          />
        </a-form-item>
      </DisabledContainer>
      <AudioPreview
        v-if="formState.wrong_answer_file_audio"
        :node-src="node?.wrong_answer_file_audio_url"
        :file="formState.wrong_answer_file_audio"
      />

      <DisabledContainer :isDisabled="disabled">
        <a-form-item
          name="before_end_call_file_audio"
          :rules="[
            { required: hasBeforeEndCallAudio, message: 'Por favor ingresar archivo de audio' },
          ]"
          class="audio-form-item-mb0"
          :validateStatus="validateAudioPickerStatusEndCall"
        >
          <template #label>
            Archivo de audio antes de finalizar la llamada

            <a-tooltip title="Este audio se reproducirá tras el cuarto intento equívoco.">
              <InfoCircleOutlined class="info-icon" />
            </a-tooltip>
          </template>

          <ArchivePicker
            v-model:value="formState.before_end_call_file_audio"
            :supportedTypes="['.mp3']"
            :disabled="!formState.has_validation"
            :maxMBSize="3"
          />
        </a-form-item>
      </DisabledContainer>
      <AudioPreview
        v-if="formState.before_end_call_file_audio"
        :node-src="node?.before_end_call_file_audio_url"
        :file="formState.before_end_call_file_audio"
      />
    </div>
  </a-form>
</template>

<script setup lang="ts">
import { provide, reactive, ref, computed, watch, onMounted } from "vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import { FormInstance } from "ant-design-vue";

import { Connector, CreateNodeBody, Node } from "@/@types/ivr";
import { DrawerFormStateKey } from "@/@types/ivr/symbols";

import { connectorServices } from "@/app/ivr/bot/services/ConnectorServices";

import ActionPicker from "@/app/ivr/bot/components/pickers/ActionPicker.vue";
import ArchivePicker from "@/app/shared/components/molecules/ArchivePicker.vue";
import AudioPreview from "@/app/ivr/bot/components/buttons/AudioPreview.vue";
import DisabledContainer from "@/app/shared/components/molecules/DisabledContainer.vue";
import { ActionType, ResponseType } from "../../../utils/enums";

const props = defineProps<{
  node?: Node;
  parentNode?: Node;
  disabled?: boolean;
  optionsConnectorApi?: Connector[];
}>();

const key = ref(0);

const validateAudioPickerStatus = computed(() => (!formState.file_audio ? "error" : ""));

// AUDIO PICKER VALIDATIONS
const validateAudioPickerStatusWrongAnswer = computed(() =>
  formState.has_validation && hasWrongAnswerAudio.value && !formState.wrong_answer_file_audio
    ? "error"
    : ""
);

const validateAudioPickerStatusEndCall = computed(() =>
  formState.has_validation && hasBeforeEndCallAudio.value && !formState.before_end_call_file_audio
    ? "error"
    : ""
);

const formRef = ref<FormInstance>();
const formState = reactive<Partial<CreateNodeBody>>({
  file_audio: props.node?.file_audio,
  variable_name: props.node?.variable_name,
  action: props.node?.action,
  has_validation: props.node?.has_validation,
  max_digit_answer: props.node?.max_digit_answer,
  wrong_answer_file_audio: props.node?.wrong_answer_file_audio,
  before_end_call_file_audio: props.node?.before_end_call_file_audio,
  api_connector: props.node?.api_connector?.id,
});

const hasWrongAnswerAudio = ref(false);
const hasBeforeEndCallAudio = ref(false);

watch(
  () => formState.has_validation,
  (newValue) => {
    hasWrongAnswerAudio.value = Boolean(newValue);
    hasBeforeEndCallAudio.value = Boolean(newValue);
  }
);

// REMOVE AUDIOS WHEN SWITCH CHANGE
watch(
  () => formState?.has_validation,
  (newVal) => {
    if (formState) {
      formState.has_validation = !newVal ? newVal : formState.has_validation;
      formState.wrong_answer_file_audio = !newVal ? null : formState.wrong_answer_file_audio;
      formState.before_end_call_file_audio = !newVal ? null : formState.before_end_call_file_audio;
      formState.max_digit_answer = !newVal ? 0 : formState.max_digit_answer;
    }
  }
);

provide(DrawerFormStateKey, formState);

const connectorSelected = computed(() => {
  if (formState.api_connector && props.optionsConnectorApi)
    return props.optionsConnectorApi.find(el => el.id == formState.api_connector);

  return null;
});
defineExpose({ formState, formRef });
</script>

<style scoped lang="sass">
@import '../../../../styles/common.sass'

p
  color: $gray-8

h3
  margin-bottom: 20px
  color: $gray-9
  font-size: 16px
  font-weight: 600
  display: flex
  justify-content: space-between
  align-items: center

.audio-preview
  width: 100%
  height: 32px
  margin-top: 10px

.divider
  margin: 0
  margin: 24px 0
  background-color: $gray-4

.input
  width: 100%

.info-icon
  margin-left: 5px
</style>
